import { CloudUpload } from "@assets/icons";
import { Badge } from "@components/ui/badge";
import { Spinner } from "@components/ui/spinner";
import { SelectProps } from "@utils/interface/index.interface";
import {
  Check,
  ChevronsUpDown,
  Crop,
  Edit,
  EyeIcon,
  File as FileIcon,
  Loader2,
  Play,
  Save,
  Trash2,
  VideoIcon,
  X,
  XIcon,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FileRejection, useDropzone } from "react-dropzone";

type DNDProps = {
  onChange: (e: any) => void;
  cropperHeight?: number;
  cropperWidth?: number;
  error?: string | boolean;
  isMulti?: boolean;
  cropperHeightPlaceholder?: number;
  cropperWidthPlaceholder?: number;
  type?: "image" | "video";
  value?: any;
  returnFile?: boolean;
};
interface SelectComponentProps {
  endpoint?: string;
  format?: {
    value: string;
    key: string;
    mutate: string;
  };

  settings?: {
    add?: boolean;
    update?: boolean;
    creatable?: boolean;
    remove?: boolean;
    debounce?: number;
  };
  onChange?: (value: any) => void;
  error?: boolean | string | undefined;
  isMulti?: boolean;
  defaultValue?: SelectProps[];
  defaultOptions?: SelectProps[];
  placeholder?: string;
  disabled?: boolean;
  [key: string]: any;
}
const AsyncSelect: React.FC<SelectComponentProps> = ({
  endpoint,
  format = { value: "id", key: "name", mutate: "name", secondary_key: "" },
  settings = {
    add: false,
    update: false,
    creatable: true,
    remove: false,
    debounce: 1000,
  },
  disabled,
  onChange,
  error,
  isMulti = false,
  defaultValue = [],
  defaultOptions = [],
  placeholder = "Search ...",
  ...props
}) => {
  const { add, remove, update, creatable } = settings;
  /* -- api calls -- */
  const { ref, inView } = useInView();
  // console.log("default valuesssss", defaultValue);
  const [search, setSearch] = useState<string>("");
  const [edit, setEdit] = useState<SelectProps | null>(null);
  const [debouncedText, setDebouncedText] = useState<string>("");
  let [options, setOptions] = useState<any[]>(endpoint ? [] : defaultOptions);
  const {
    data,
    hasNextPage,
    isFetchingNextPage,
    status,
    fetchNextPage,
    refetch,
  } = useInfiniteScroller({
    endpoint,
    filter: { search: debouncedText, ...props.filter },
    enabled: endpoint ? true : false,
    key: `${endpoint}-async`,
  });

  let allScrolledData = useMemo(() => {
    return data?.pages.flatMap((page: any) => page.results) || [];
  }, [data]);
  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState(defaultValue);

  useEffect(() => {
    if (Array.isArray(allScrolledData) && endpoint) {
      const formatData = formatSelectData(
        Array.isArray(allScrolledData) ? allScrolledData : [],
        format.key,
        format.value,
        // @ts-ignore
        format.secondary_key
      );
      setOptions(() => formatData);
    }
  }, [allScrolledData]);

  useEffect(() => {
    if (!Array.isArray(defaultValue)) return;
    /* keep the previous reference when nothing changed, otherwise an
       undefined field value (new [] on every render) loops forever */
    setValue((prev: SelectProps[]) =>
      prev?.length === defaultValue.length &&
      prev.every((item, index) => item?.value === defaultValue[index]?.value)
        ? prev
        : defaultValue
    );
  }, [defaultValue]);
  // console.log("value", value);
  useEffect(() => {
    const debounce = setTimeout(() => {
      setDebouncedText(() => search);
    }, 1000);
    return () => clearTimeout(debounce);
  }, [search]);

  // const handleSetValue = (val: any) => {
  //   if (!isMulti) {
  //     setValue([val]);
  //     onChange?.([val]);
  //     setOpen(false);
  //     return;
  //   }
  //   let findItem = value?.some(
  //     (item: SelectProps) => item["value"] === val["value"]
  //   );
  //   if (!findItem) {
  //     setValue((prev: SelectProps[]) => [...prev, val]);
  //     onChange?.([...value, val]);
  //     console.log("this is Value 2", value);
  //   } else {
  //     let removed = value?.filter(
  //       (item: SelectProps) => item["value"] !== val["value"]
  //     );
  //     setValue(() => [...removed]);
  //     console.log("this is Value 3", value);
  //   }
  // };

  const handleSetValue = (val: any) => {
    if (!isMulti) {
      setValue([val]);
      onChange?.([val]);
      setOpen(false);
      return;
    }

    let findItem = value?.some(
      (item: SelectProps) => item["value"] === val["value"]
    );

    if (!findItem) {
      // Adding item
      const newValue = [...value, val];
      setValue(newValue);
      onChange?.(newValue);
      console.log("this is Value 2", newValue);
    } else {
      // Removing item
      const removed = value?.filter(
        (item: SelectProps) => item["value"] !== val["value"]
      );
      setValue(removed);
      onChange?.(removed); // Use the filtered array, not the old value
      console.log("this is Value 3", removed);
    }
  };

  /* */
  const { mutate: deleteMutate, isPending: deletePending } = useDeleteData();
  const onDelete = ({ item }: { item: SelectProps }) => {
    deleteMutate(
      {
        id: item["value"],
        endpoint,
      },
      {
        // @ts-ignore
        onSuccess: ({ status, data: updateData }, data) => {
          setValue((prev: SelectProps[]) => {
            const exists = prev.findIndex((s) => s?.value === item.value);
            if (exists !== -1) {
              return [...prev.slice(0, exists), ...prev.slice(exists + 1)];
            }
            return prev;
          });
          refetch();

          // console.log("🚀 ~ onDelete ~ updateData:", data)
          // setValue((prev) =>
          //   prev.filter((item: any) => item["value"] !== data["id"])
          // );
        },
      }
    );
  };

  const { mutate, isPending: mutatePending } = usePostData();

  const onSubmit = () => {
    if (creatable) {
      let newData = {
        label: search,
        value: search,
      };
      setOptions(() => [newData]);
      setValue((prev: SelectProps[]) => [...prev, newData]);
      setSearch("");
      onChange?.([newData]);
      if (!isMulti) {
        setOpen(false);
      }
      return;
    } else {
      mutate(
        {
          // @ts-ignore
          data: { [format.mutate]: search },
          endpoint: endpoint,
        },
        {
          // @ts-ignore
          onSuccess: ({ status, data: updateData }) => {
            let newData = {
              label: updateData[format.mutate],
              value: updateData["id"],
            };
            isMulti
              ? setValue((prev: SelectProps[]) => [...prev, newData])
              : setValue(() => [newData]);
            // allScrolledData.unshift(updateData);
            if (!isMulti) {
              setOpen(false);
            }
            onChange?.(isMulti ? [...value, newData] : [newData]);
            refetch();
          },
        }
      );
    }
  };

  const onUpdate = () => {
    mutate(
      {
        // @ts-ignore
        data: { [format.mutate]: edit?.label, id: edit?.value },
        endpoint: endpoint,
        method: METHODS.PATCH,
      },
      {
        // @ts-ignore
        onSuccess: ({ status, data: updateData }) => {
          let newData = options.map((sitem) =>
            sitem.value == edit?.value ? edit : sitem
          );
          setOptions(() => newData);
          setValue((prev: SelectProps[]) =>
            prev?.map((item) => (item.value === edit?.value ? edit : item))
          );
          setEdit(null);
        },
      }
    );
  };

  return (
    <div
      className={cn([
        "w-full",
        props.className,
        `${disabled && "cursor-not-allowed"}`,
      ])}
    >
      <Popover open={open} modal={false} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            disabled={disabled}
            aria-expanded={open}
            className={cn(
              [" w-full justify-between h-fit"],
              `${error && "border-red-500"}`,
              `${disabled && "cursor-not-allowed"}`
            )}
            onClick={() => {
              setOpen((prev) => !prev);
            }}
          >
            {open && value?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {" "}
                {value?.slice(0, 5).map((item: SelectProps, index: number) => (
                  <span
                    key={index}
                    className="text-white flex justify-center cursor-pointer items-center gap-2 rounded-full text-[14px] px-2 bg-[#1da077] max-w-[250px] truncate"
                  >
                    {item.label}
                  </span>
                ))}
                {value?.length > 5 && (
                  <Badge className="cursor-pointer bg-[#a7aba9] text-white hover:bg-[#1aa178] transition-all">
                    +{value?.length - 5} more
                  </Badge>
                )}
              </div>
            ) : (
              "Select Options"
            )}

            {isFetchingNextPage ? (
              <Spinner />
            ) : (
              <ChevronsUpDown className="opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom" // Default to bottom
          sideOffset={5}
          align="start"
          avoidCollisions={true} // This will automatically flip to top if no space below
          collisionPadding={10} // Padding from viewport edges
          portal={false}
          className=" border-none p-0 !w-[var(--radix-popper-anchor-width)]"
        >
          <div className="relative w-full  p-[10] ">
            <Input
              type="text"
              value={search}
              maxLength={90}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:outline-none shadow-none focus:ring-2 focus:ring-blue-500"
              placeholder={placeholder}
            />

            <div className="absolute mt-1 w-full rounded-md bg-white shadow-lg max-h-[250px] overflow-y-auto border border-gray-200 z-50">
              {options?.length > 0
                ? options?.map((framework) => (
                    <li
                      key={framework.value}
                      className="flex items-center group px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log("THIS Is the framework", framework);
                        !edit && handleSetValue(framework);
                      }}
                    >
                      {edit && framework.value === edit.value ? (
                        <input
                          className="outline-none max-w-[500px] border-b border-[#1aa178]"
                          type="text"
                          autoFocus
                          maxLength={90}
                          value={edit.label}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setEdit((prev: any) => ({
                              ...prev,
                              ["label"]: e.target.value,
                            }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault(); // prevent form submission
                              if (edit && edit.label) onUpdate();
                            }
                          }}
                        />
                      ) : (
                        <Tooltip description={framework.label}>
                          <div className="truncate w-fit max-md:max-w-[250px] max-w-[350px] overflow-hidden">
                            <span className="flex flex-col  w-full gap-2">
                              {framework.label}{" "}
                              {framework?.secondary_label && (
                                <Badge
                                  variant={"secondary"}
                                  className="text-yellow-500"
                                >
                                  {framework?.secondary_label}
                                </Badge>
                              )}
                            </span>
                          </div>
                        </Tooltip>
                      )}

                      <Check
                        className={cn(
                          "ml-auto h-4 w-4 transition-opacity",
                          value?.some(
                            (item: SelectProps) =>
                              item["value"] === framework.value
                          )
                            ? "opacity-100 text-green-500"
                            : "opacity-0"
                        )}
                      />
                      {defaultOptions.length == 0 && update && (
                        <div
                          className="ml-2 bg-slate-100 text-slate-600  group-hover:flex hidden items-center justify-center h-5 w-5 rounded cursor-pointer hover:bg-slate-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEdit(framework);
                          }}
                        >
                          {mutatePending ? (
                            <Spinner className="h-3.5 w-3.5 text-slate-500" />
                          ) : edit && framework.value === edit.value ? (
                            <Save
                              onClick={() =>
                                edit && !edit.label
                                  ? () => alert("must have value")
                                  : onUpdate()
                              }
                              className="h-3.5 w-3.5 text-slate-500"
                            />
                          ) : (
                            <Edit className="h-3.5 w-3.5 text-slate-500" />
                          )}
                        </div>
                      )}
                      {defaultOptions.length == 0 && remove && (
                        <div
                          className="ml-2     items-center justify-center h-5 w-5  group-hover:flex hidden rounded cursor-pointer transition-all "
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete({ item: framework });
                          }}
                        >
                          {deletePending ? (
                            <Spinner className="h-3.5 w-3.5 text-red-500 group-hover:bg-red-100 transition-all" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5 text-red-500  group-hover:bg-red-100 transition-all" />
                          )}
                        </div>
                      )}
                    </li>
                  ))
                : null}

              {endpoint && defaultOptions.length == 0 && (
                <>
                  <li ref={ref} className="flex flex-col space-y-2 ">
                    {isFetchingNextPage ? (
                      <>
                        {Array.from({ length: 4 }).map((_, key) => (
                          <Skeleton
                            className="h-[30px] w-full rounded-[4px]"
                            key={key}
                          />
                        ))}
                      </>
                    ) : hasNextPage ? (
                      Array.from({ length: 4 }).map((_, key) => (
                        <Skeleton
                          className="h-[30px] w-full rounded-[4px]"
                          key={key}
                        />
                      ))
                    ) : //@ts-ignore
                    data?.pages[0]?.count <= options.length ? (
                      <p className="text-center text-gray-500 py-2">
                        You have reached the end ~
                      </p>
                    ) : (
                      <Skeleton className="h-[30px] w-full rounded-[4px] mt-5" />
                    )}
                  </li>
                  <div className="px-3 py-2 text-sm text-gray-500">
                    {["pending"].includes(status) ? (
                      "Searching..."
                    ) : ["success"].includes(status) && options.length === 0 ? (
                      <span>
                        No record found {search && `'${search}'`}
                        {add && search && endpoint && (
                          <>
                            ,{" "}
                            <i
                              className="cursor-pointer underline text-blue-600 hover:text-blue-800"
                              onClick={onSubmit}
                              onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key === "Enter") onSubmit();
                              }}
                            >
                              {mutatePending ? "creating..." : "create new"}
                            </i>
                          </>
                        )}
                      </span>
                    ) : (
                      options.length === 0 && "No framework found."
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {!open && value?.length > 0 && (
        <div className="flex flex-wrap gap-2 my-2">
          {value?.slice(0, 5).map((item: SelectProps, index: number) => (
            <span
              key={index}
              onClick={() => {
                const isRemovalDisabled =
                  props?.disableOptionsForAsync?.some(
                    (value: string | number) => value === item?.value
                  ) ?? false;

                if (isRemovalDisabled && props?.onRemove?.(item) !== true) {
                  return;
                }

                setValue((prev: SelectProps[]) => {
                  const exists = prev.findIndex((s) => s?.value === item.value);
                  if (exists !== -1) {
                    onChange?.(prev.filter((s) => s?.value !== item.value));
                    return [
                      ...prev.slice(0, exists),
                      ...prev.slice(exists + 1),
                    ];
                  }

                  return prev;
                });
              }}
              className="text-white flex justify-center cursor-pointer items-center gap-2 rounded-full text-[14px] px-2 bg-[#1da077] max-w-[250px] truncate"
            >
              <span className="block w-fit max-w-[100px] truncate">
                {item.label}
              </span>{" "}
              <XIcon size={12} className="cursor-pointer" />
            </span>
          ))}
          {value?.length > 5 && (
            <Badge className="cursor-pointer bg-[#1da077] text-white hover:bg-[#1aa178] transition-all">
              +{value?.length - 5} more
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};

const maxLength = 150;
const maxSize = Number(import.meta.env.VITE_MAX_FILE_SIZE || 200) * 1024 * 1024;

const DndUpload: React.FC<DNDProps> = ({
  onChange,
  error,
  isMulti = false,
  cropperHeight = 100,
  cropperWidth = 100,
  cropperHeightPlaceholder = 1100,
  cropperWidthPlaceholder = 1100,
  value,
  type = "image",
  returnFile = false,
}) => {
  const [acceptedFiles, setAcceptedFiles] = useState<(File | Blob)[]>([]);
  const [fileRejections, setFileRejections] = useState<FileRejection[]>([]);
  const [imageUrlInput, setImageUrlInput] = useState<string>("");
  const [invalidUrlErr, setinvalidUrlErr] = useState<boolean>();


  useEffect(() => {
    if (
      value &&
      Array.isArray(value) &&
      value?.every((item) => item instanceof File || item instanceof Blob)
    ) {
      setAcceptedFiles(value as any[]);
    }
    if ((value && value instanceof File) || value instanceof Blob) {
      setAcceptedFiles(() => [value]);
    }
  }, [value]);

  const handleManualUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const imageExtensions = /\.(jpeg|jpg|png|gif|webp|svg)$/i;
    const videoExtensions = /\.(mp4|webm|ogg|mov|avi|mkv)$/i;

    const url = e.target.value;
    setImageUrlInput(url);
    setinvalidUrlErr(true);

    // If the URL is a valid image, update the change handler
    if (
      (type === "image" && imageExtensions.test(url)) ||
      (type === "video" && videoExtensions.test(url))
    ) {
      setinvalidUrlErr(false);
      onChange(url);
    }
  };

  function nameLengthValidator(file: File) {
    if (file.name.length > maxLength) {
      return {
        code: "name-too-large",
        message: `Name is larger than ${maxLength} characters`,
      };
    }

    if (file.size > maxSize) {
      return {
        code: "size-too-large",
        message: `Size is larger than ${maxSize / (1024 * 1024)} MB`,
      };
    }

    return null;
  }

  const [imageURL, setImageURL] = useState<string[]>([]);

  const {
    mutate: uploadToGalleryMutation,
    isPending,
    isSuccess,
  } = useMutation({
    mutationFn: uploadToGallery,
    onSuccess: (response) => {
      // Extract the image URL/ID from the response - adjust based on your API response structure
      const uploadedImageUrl = response.data?.image || response.data?.url;
      setImageUrlInput(uploadedImageUrl);
      setImageURL((prev) => {
        onChange(isMulti ? [...prev, uploadedImageUrl] : uploadedImageUrl);
        return [...prev, uploadedImageUrl];
      });

      showToast({
        title: "Uplaoded Successfully",
        description: "File has been uploaded successfully",
        type: "success",
      });

      console.log("This is the uploaded image url", uploadedImageUrl);
      // @ts-ignore
    },
    onError: (error: any) => {
      showToast({
        title: "Uplaoded Failed",
        description: "Error" + error?.message || "File upload failed",
        type: "error",
      });
      setAcceptedFiles([]);
      // setIsUploading(false);
      // Handle error - show toast notification, etc.
    },
  });

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (returnFile) {
        onChange(isMulti ? acceptedFiles : acceptedFiles[0]);
        setAcceptedFiles(acceptedFiles);
        setFileRejections([]);
        return;
      }

      const formData = new FormData();
      formData.append("image", acceptedFiles[0]);
      await uploadToGalleryMutation({ formData });
      setAcceptedFiles(acceptedFiles);
      setFileRejections([]);
    },
    [onChange, isMulti, imageURL, returnFile]
  );

  // const onDrop = useCallback(async (acceptedFiles: File[]) => {
  //   const formData = new FormData();
  //   formData.append("image", acceptedFiles[0]);
  //   await uploadToGalleryMutation({ formData });
  //   console.log("This has ran after the mutation")
  //   // @ts-ignore
  //   onChange(isMulti ? imageURL : imageURL[0]);
  //   setAcceptedFiles(acceptedFiles);
  //   setFileRejections([]);
  // }, []);

  const onDropRejected = useCallback((rejectedFiles: FileRejection[]) => {
    setFileRejections(rejectedFiles);
    setAcceptedFiles([]);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      ...(type === "image" && {
        "image/png": [".png", ".jpg", ".jpeg", ".gif", ".svg", ".webp"],
      }),
      ...(type === "video" && {
        "video/mp4": [".mp4", ".mov", ".mpeg"],
      }),
    },
    validator: nameLengthValidator,
    maxFiles: 1,
    onDrop,
    onDropRejected,
  });

  /* cropped */
  const [crop, setCrop] = useState<any>();
  const [isCropOpen, setisCropOpen] = useState<boolean | null>(false);

  const handleCrop = (file: File | Blob, index: number) => {
    const objectUrl = URL.createObjectURL(file);
    setCrop({ file, url: objectUrl, index });
    // @ts-ignore
    onChange(file);
    setisCropOpen(true);
  };
  const [localVideoUrl, setLocalVideoUrl] = useState<string>("");
  const acceptedFileItems =
    Array.isArray(acceptedFiles) &&
    acceptedFiles?.map((file, index) => {
      const localURl = URL.createObjectURL(file);

      return (
        <div
          className="border p-[10px] rounded-[6px] flex items-center justify-between gap-2 text-start bg-accent min-w-[200px]"
          key={file instanceof File ? file.name : ""}
        >
          <div className="flex items-center gap-2">
            <div className="">
              {type == "video" ? <VideoIcon /> : <FileIcon />}
            </div>
            <div className="text-[12px] text-[#969696]">
              <div className="flex">
                <div className="max-w-[150px] truncate">
                  {file instanceof File ? file.name : ""}
                </div>
              </div>
              <ul>
                <li className="text-[12px] font-[500]">
                  {file.size < 1024 * 1024
                    ? (file.size / 1024).toFixed(1) + " KB"
                    : (file.size / (1024 * 1024)).toFixed(1) + " MB"}
                </li>
              </ul>
            </div>
          </div>

          <div className="flex gap-2">
            {type == "video" ? (
              <div
                onClick={() => {
                  setLocalVideoUrl(localURl);
                  setisVideoOpen(true);
                }}
                className="text-[#1aa178] cursor-pointer p-[4px] rounded-[4px]"
              >
                <EyeIcon size={14} />
              </div>
            ) : (
              <div
                className="bg-[#1aa178] cursor-pointer p-[4px] rounded-[4px]"
                onClick={() => handleCrop(file, index)}
              >
                <Crop size={14} color="white" />
              </div>
            )}
            <div
              className="bg-slate-50 cursor-pointer p-[4px] rounded-[4px]"
              onClick={() => handleRemoveAccepted(index)}
            >
              <X size={14} />
            </div>
          </div>
        </div>
      );
    });

  const handleRemoveRejection = (indexToRemove: number) => {
    setFileRejections((prevRejections) =>
      prevRejections.filter((_, index) => index !== indexToRemove)
    );
  };
  const handleRemoveAccepted = (indexToRemove: number) => {
    setImageUrlInput("");
    setData(null);
    onChange(null);
    setAcceptedFiles((prevRejections) =>
      prevRejections.filter((_, index) => index !== indexToRemove)
    );
  };
  const [isVideoOpen, setisVideoOpen] = useState<boolean | null>(null);
  const fileRejectionItems = fileRejections.map(({ file, errors }, index) => (
    <div
      className="border p-[10px] rounded-[6px] flex items-center justify-between  gap-2 text-start bg-accent min-w-[200px]"
      key={file.path}
    >
      <div className="flex items-center gap-2">
        <div className="">{type == "video" ? <VideoIcon /> : <FileIcon />}</div>
        <div className="text-[12px] text-[#969696]">
          <div className="flex">
            <div className="max-w-[150px] truncate">{file.name}</div> -{" "}
            {file.size < 1024 * 1024
              ? (file.size / 1024).toFixed(1) + " KB"
              : (file.size / (1024 * 1024)).toFixed(1) + " MB"}
          </div>
          <ul>
            {errors.map((e) => (
              <li className="text-[12px] font-[500] text-red-500" key={e.code}>
                {e.message}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div
        className="bg-slate-50 cursor-pointer p-[4px] rounded-[4px]"
        onClick={() => handleRemoveRejection(index)}
      >
        <X size={14} />
      </div>
    </div>
  ));
  const dropzoneProps =
    fileRejectionItems.length === 0 &&
    Array.isArray(acceptedFileItems) &&
    acceptedFileItems?.length === 0
      ? getRootProps()
      : {};
  const [data, setData] = useState<String | Blob | null>(null);

  const dataUrl = useMemo(() => {
    if (!value) return undefined;
    if (typeof value === "string") return value;
    if (value instanceof File || value instanceof Blob)
      return URL.createObjectURL(value);
    return;
  }, [value]);

  return (
    <>
      <div
        {...dropzoneProps}
        className={`${
          error ? "border-red-500 " : ""
        }p-[20px] h-[232px] border-[1.5px] border-[#E5E7EB] border-dashed rounded-[8px] min-h-[100px] overflow-auto w-full flex items-center relative ${
          fileRejectionItems.length > 0
            ? "border-red-500 bg-red-50"
            : `${error ? "bg-red-500/10" : "hover:bg-[#1DA077]/10 "}`
        } ${data && "bg-[#1DA077]"}`}
        style={{
          backgroundImage: type === "video" ? "" : `url(${dataUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Black overlay when image exists */}
        {(data || value) && (
          <div className="absolute inset-0 bg-black/50 rounded-[8px]" />
        )}

        <div
          className={cn(
            "flex flex-col items-center text-center  my-auto max-h-full w-full relative ",
            `${error ? "text-red-500" : "text-[#1DA077]"}`
          )}
        >
          {fileRejectionItems.length === 0 ? (
            <>
              <CloudUpload
                className={cn(isPending && "animate-bounce")}
                color={
                  error ? "#fb2c36 " : data || value ? "#ffffff" : "#1DA077"
                }
              />
              {isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <>
                  <p
                    className={`text-[14px] font-[500] ${
                      data
                        ? "text-white"
                        : value &&
                          `${
                            error
                              ? "text-red-500"
                              : value
                              ? "text-white"
                              : "text-[#1DA077]"
                          }`
                    }`}
                  >
                    Drag and drop a {type} to upload
                  </p>
                  <p
                    className={`text-[14px] text-wrap font-[500] ${
                      data || value
                        ? "text-white"
                        : value &&
                          `${error ? "text-red-500" : "text-[#1DA077]"}`
                    }`}
                  >
                    {type == "video" ? "MP4 Mov and MPEG" : "PNG, JPG and JPEG"}{" "}
                    up to {maxSize / (1024 * 1024)} MB
                    <br /> Recommended Size: {cropperHeightPlaceholder} x{" "}
                    {cropperWidthPlaceholder} px
                  </p>
                </>
              )}
            </>
          ) : (
            <aside className="my-3">
              <div className="flex flex-wrap gap-[10px]">
                {acceptedFileItems}
              </div>
              <div className="flex flex-wrap gap-[10px]">
                {fileRejectionItems}
              </div>
            </aside>
          )}

          <aside className="my-3">
            <div className="flex flex-wrap gap-[10px]">{acceptedFileItems}</div>
          </aside>
          {type === "video" &&
            typeof value === "string" &&
            value.length > 0 && (
              <div
                className="relative"
                onClick={(e) => {
                  e.stopPropagation();
                  setisVideoOpen((prev) => !prev);
                }}
              >
                <div className="absolute top-1/2 left-1/2 rounded-full -translate-x-1/2 -translate-y-1/2 bg-[#1DA077] size-[30px] text-white flex justify-center items-center ">
                  <Play size={17} />
                </div>
                <video
                  src={value}
                  className="w-full h-[100px] cursor-pointer rounded-md mt-4"
                />
              </div>
            )}
        </div>

        <input
          {...getInputProps({
            multiple: isMulti,
          })}
        />
      </div>

      <Dialog
        className="bg-slate-100 shadow-none border-none min-w-[600px]"
        open={!!isVideoOpen}
        setOpen={() => {
          setisVideoOpen(false);
        }}
      >
        <video
          src={localVideoUrl || value}
          controls
          controlsList="nodownload"
          autoPlay
          className="w-full h-full object-cover  cursor-pointer  rounded-md "
        />
      </Dialog>
      <Dialog
        className=""
        open={!!isCropOpen}
        title="Crop Image"
        setOpen={() => setisCropOpen(null)}
      >
        {crop && (
          <ImgCropper
            output="blob"
            src={crop.url}
            setData={setData}
            // @ts-ignore
            data={data}
            cropperHeight={cropperHeight}
            cropperWidth={cropperWidth}
            onSave={(data) => {
              onChange(data);
              setCrop(data);
              setisCropOpen(false);
            }}
          />
        )}
      </Dialog>
      <div className="mt-4">
        <label
          htmlFor="image-url"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {type == "image" && "Or enter an image URL:"}
          {type == "video" && "Or enter a video URL:"}
        </label>
        <input
          id="image-url"
          type="text"
          value={imageUrlInput}
          onChange={handleManualUrlChange}
          placeholder={
            type == "image"
              ? "https://example.com/image.jpg"
              : "https://example.com/video.mp4"
          }
          className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring focus:ring-green-200"
          disabled={isSuccess}
        />
        {invalidUrlErr && (
          <span className="text-[12px] font-[500] text-red-500">
            {type == "image" && "InValid URL: It must end with image extension"}
            {type == "video" && "InValid URL: It must end with video extension"}
          </span>
        )}
      </div>
    </>
  );
};

/* otp */
type OtpProps = {
  label: string;
  error: string | boolean;
  length: number;
  [key: string]: any;
};

const OTPInput: React.FC<OtpProps> = ({
  label = "",
  error = false,
  length,
  ...props
}) => {
  return (
    <div>
      {label && <label className="text-[14px]">{label}</label>}
      <InputOTP maxLength={length} {...props}>
        <InputOTPGroup className="gap-[10px]">
          {Array.from({ length }).map((_, index: number) => (
            <InputOTPSlot
              index={index}
              className="border h-[59px] w-[59px] !rounded-[4px]"
              aria-invalid={error ? true : false}
            />
          ))}
        </InputOTPGroup>
      </InputOTP>
      {error && typeof error === "string" && <ErrorLabel message={error} />}
    </div>
  );
};

/* cropper */

import ReactCrop, { Crop as CropProp, PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

function canvasPreview(
  image: HTMLImageElement,
  crop: PixelCrop
): HTMLCanvasElement | null {
  const canvas = document.createElement("canvas");
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  canvas.width = crop.width || 0;
  canvas.height = crop.height || 0;

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const cropX = crop.x || 0;
  const cropY = crop.y || 0;
  const cropWidth = crop.width || 0;
  const cropHeight = crop.height || 0;

  ctx.drawImage(
    image,
    cropX * scaleX,
    cropY * scaleY,
    cropWidth * scaleX,
    cropHeight * scaleY,
    0,
    0,
    cropWidth,
    cropHeight
  );

  return canvas;
}

export function ImgCropper({
  src,
  onSave,
  output,
  setData,
  cropperHeight,
  cropperWidth,
  data,
}: {
  src: string;
  output: "blob" | "base64";
  onSave: (data: File | Blob) => void;
  setData: (data: string | Blob) => void;
  data: string | Blob;
  cropperHeight?: number;
  cropperWidth?: number;
}) {
  const [crop, setCrop] = useState<CropProp>({
    unit: "px",
    width: cropperWidth as number,
    height: cropperHeight as number,
    x: 0,
    y: 0,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const onImageLoad = useCallback((img: HTMLImageElement) => {
    imageRef.current = img;
  }, []);

  const handleCrop = () => {
    // inside handleCrop (replace your existing canvas.toBlob call)

    if (imageRef.current && completedCrop) {
      const canvas = canvasPreview(imageRef.current, completedCrop);
      if (!canvas) return;

      canvas.toBlob(
        (blob) => {
          if (blob) {
            // wrap the blob in a File so Dropzone will recognize it
            const jpegFile = new File([blob], "cropped.jpg", {
              type: "image/jpeg",
            });

            // update the preview
            setData(jpegFile);

            // when the user clicks “Save”
            onSave(jpegFile);
          }
        },
        "image/jpeg",
        1
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="">
        <ReactCrop
          crop={crop}
          className=" object-contain"
          onChange={(newCrop) => setCrop(newCrop)}
          onComplete={(c) => {
            setCompletedCrop(c);
          }}
          minHeight={cropperHeight || 200}
          minWidth={cropperWidth || 200}
        >
          <img
            src={src}
            alt="Crop me"
            className="object-contain w-full h-[400px] "
            onLoad={(e) => onImageLoad(e.currentTarget)}
          />
        </ReactCrop>
      </div>
      <div className="flex justify-center space-x-2">
        <Button onClick={handleCrop}>Crop Image</Button>
        {data && (
          // @ts-ignore
          <Button variant="ghost" onClick={() => onSave(data)}>
            Save
          </Button>
        )}
      </div>

      {data && (
        <div className="space-y-4">
          {data && (
            <>
              <ZoomImage>
                <div className="border rounded-md hover:outline-[#1aa178]/50 hover:outline-2 overflow-hidden h-[70px] w-[100px] bg-slate-100">
                  <img
                    src={
                      typeof data === "string"
                        ? data
                        : output === "blob"
                        ? URL.createObjectURL(data)
                        : ""
                    }
                    alt="Cropped"
                    className="h-full w-full object-contain"
                  />
                </div>
              </ZoomImage>
            </>
          )}
        </div>
      )}
    </div>
  );
}

import { cn } from "@/lib/utils";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@components/ui/input-otp";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/ui/popover";
import { Skeleton } from "@components/ui/skeleton";
import { useInfiniteScroller } from "@pages/dashboard/test/hook";
import Dialog from "@ui/external/components/dialog";
import { formatSelectData, showToast } from "@utils/helper/index.helper";
import { useDeleteData, usePostData } from "@utils/query/index.query";
import { useInView } from "react-intersection-observer";
import { Controlled as ControlledZoom } from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import { ErrorLabel } from "../label";
import { METHODS } from "@services/interface.service";
import Tooltip from "@ui/external/components/tooltip";
import UploadToCloud from "../filetocloud";
import { useMutation } from "@tanstack/react-query";
import { uploadToGallery } from "@services/giveAwayServices";
import { toast } from "sonner";
// import { _ } from "node_modules/react-router/dist/development/fog-of-war-D2zsXvum.d.mts";

interface ImageZoomProps {
  children: React.ReactElement;
  [key: string]: any;
}
const ZoomImage: React.FC<ImageZoomProps> = ({
  children,
  videoUrl,
  ...props
}) => {
  const [isZoomed, setIsZoomed] = useState(false);

  const handleZoomChange = useCallback((shouldZoom: boolean) => {
    setIsZoomed(shouldZoom);
  }, []);

  return (
    <ControlledZoom
      isZoomed={isZoomed}
      onZoomChange={handleZoomChange}
      {...props}
    >
      {videoUrl ? <video src={videoUrl}></video> : children}
    </ControlledZoom>
  );
};

export { AsyncSelect, DndUpload, OTPInput, ZoomImage };
